import {useContext, useEffect, useRef} from 'react'
import {useMap} from 'react-leaflet'
import {simulatedPosition} from '../data/current-location'
import {MapContext} from '../context/MapContext'

const initialPanelHeights = {close: 0.12, open: 0.5, full: 1}

const getInitialViewCenter = (map, coords, panelStatus, zoom) => {
  const viewportHeight = window.visualViewport?.height || window.innerHeight
  const panelHeight = initialPanelHeights[panelStatus] ?? initialPanelHeights.open
  const targetY = viewportHeight * (1 - panelHeight) / 2
  const containerTop = map.getContainer().getBoundingClientRect().top
  const targetContainerY = targetY - containerTop
  const mapSize = map.getSize()
  const userPoint = map.project(coords, zoom)

  return map.unproject([
    userPoint.x,
    userPoint.y + mapSize.y / 2 - targetContainerY,
  ], zoom)
}

const centerInitialPosition = (map, coords, panelStatus) => {
  const viewportHeight = window.visualViewport?.height || window.innerHeight
  const panelHeight = initialPanelHeights[panelStatus] ?? initialPanelHeights.open
  const targetY = viewportHeight * (1 - panelHeight) / 2
  const containerTop = map.getContainer().getBoundingClientRect().top
  const currentY = map.latLngToContainerPoint(coords).y + containerTop

  map.panBy([0, currentY - targetY], {animate: false})
}

const UpdateMapView = ({coords, focus, panelStatus, zoom = 17}) => {
  const map = useMap()
  const initialPanelStatus = useRef(panelStatus)
  const {setIsRouteReady} = useContext(MapContext)

  useEffect(() => {
    map.setView(coords, zoom)

    const frame = requestAnimationFrame(() => {
      centerInitialPosition(map, coords, initialPanelStatus.current)
    })

    return () => cancelAnimationFrame(frame)
  }, [coords, map, zoom])

  useEffect(() => {
    if (focus?.reset) {
      setIsRouteReady(false)
      const frame = requestAnimationFrame(() => {
        map.flyTo(
          getInitialViewCenter(map, coords, initialPanelStatus.current, zoom),
          zoom,
          {duration: 0.7, easeLinearity: 0.25},
        )
      })

      return () => cancelAnimationFrame(frame)
    }

    if (!focus?.coords?.length) return

    setIsRouteReady(false)
    const showRoute = () => setIsRouteReady(true)
    map.once('moveend', showRoute)

    map.flyToBounds([...focus.coords, simulatedPosition], {
      padding: [56, 56],
      maxZoom: 18,
      duration: 0.75,
    })

    return () => map.off('moveend', showRoute)
  }, [coords, focus, map, setIsRouteReady, zoom])

  return null
}

export default UpdateMapView
